// Layers, the Photoshop way: a stack you can show, hide, lock, reorder, blend and merge.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowDownToLine, Copy, Eye, EyeOff, GripVertical, Lock, LockOpen, MousePointerSquareDashed, Plus, SquareArrowDown, Trash2, X
} from "lucide-react";
import type { BlendMode, LayerElement } from "../../shared/model";
import type { Editor } from "../editor/editor";
import { renderToCanvas, contentBox, releaseCanvas } from "../editor/export";
import { useEditor, useStore, useUi } from "../editor/react/hooks";
import { PAPERS } from "../editor/style";
import { stickerUrl } from "../data/stickers";
import { IconButton, Panel, WavySlider } from "../ui/kit";

export const BLEND_MODES: { key: BlendMode; label: string }[] = [
  { key: "normal", label: "Normal" },
  { key: "multiply", label: "Multiply" },
  { key: "screen", label: "Screen" },
  { key: "overlay", label: "Overlay" },
  { key: "darken", label: "Darken" },
  { key: "lighten", label: "Lighten" },
  { key: "color-burn", label: "Colour burn" },
  { key: "color-dodge", label: "Colour dodge" },
  { key: "soft-light", label: "Soft light" },
  { key: "difference", label: "Difference" },
  { key: "luminosity", label: "Luminosity" }
];

interface LayerRow {
  layer: LayerElement;
  count: number;
  hasSelection: boolean;
}

function useLayerRows(ed: Editor): LayerRow[] {
  const sub = useCallback(
    (fn: () => void) => {
      const offs = [ed.doc.subscribe(fn), ed.subscribeUi(fn)];
      return () => offs.forEach((o) => o());
    },
    [ed]
  );
  return useStore(
    sub,
    () => {
      const sel = new Set(ed.ui.selection);
      const counts = new Map<string, number>();
      const selected = new Set<string>();
      for (const e of ed.doc.all()) {
        const l = ed.layerOf(e);
        counts.set(l, (counts.get(l) ?? 0) + 1);
        if (sel.has(e.id)) selected.add(l);
      }
      return ed.layers().map((layer) => ({ layer, count: counts.get(layer.id) ?? 0, hasSelection: selected.has(layer.id) }));
    },
    (a, b) => a.length === b.length && a.every((r, i) => r.layer === b[i].layer && r.count === b[i].count && r.hasSelection === b[i].hasSelection)
  );
}

/** A small preview of what is on one layer, framed like the whole board so layers line up. */
function LayerThumb({ layerId }: { layerId: string }) {
  const ed = useEditor();
  const [src, setSrc] = useState<string | null>(null);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  useEffect(() => {
    let timer = 0;
    let live = true;
    const draw = () => {
      const all = ed.doc.all();
      const frame = contentBox(all);
      const mine = all.filter((e) => ed.layerOf(e) === layerId);
      if (!frame || !mine.length) {
        setSrc(null);
        return;
      }
      const pad = Math.max(frame.w, frame.h) * 0.04;
      const box = { x: frame.x - pad, y: frame.y - pad, w: frame.w + pad * 2, h: frame.h + pad * 2 };
      renderToCanvas(mine, {
        bg: ed.bg, box, padding: 0, scale: 120 / Math.max(box.w, box.h * 1.5), transparent: true, grid: false,
        resolveImage: (s) => ed.host.resolveImage(s), stickerUrl, maxPixels: 120 * 80 * 1.5
      })
        .then((c) => {
          if (live) setSrc(c.toDataURL("image/png"));
          releaseCanvas(c);
        })
        .catch(() => undefined);
    };
    draw();
    const off = ed.doc.subscribe((c) => {
      // redraw a little after edits settle, and only when this layer (or the board's extent) may have changed
      window.clearTimeout(timer);
      timer = window.setTimeout(draw, c.origin === "local" ? 700 : 1200);
    });
    return () => {
      live = false;
      off();
      window.clearTimeout(timer);
    };
  }, [ed, layerId]);
  return <span className={"layer-thumb" + (dark ? " dark" : "")}>{src && <img src={src} alt="" draggable={false} />}</span>;
}

function LayerName({ layer, onRename }: { layer: LayerElement; onRename: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(layer.name);
  const ref = useRef<HTMLInputElement>(null);
  const lastTap = useRef(0);
  useLayoutEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);
  if (editing) {
    return (
      <input
        ref={ref}
        className="layer-name-input"
        value={draft}
        maxLength={60}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const t = draft.trim();
          if (t && t !== layer.name) onRename(t);
          else setDraft(layer.name);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            setDraft(layer.name);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <span
      className="layer-name"
      title="Double-tap to rename"
      onPointerUp={() => {
        // a quick second tap renames (works for fingers, the pencil and the mouse alike)
        const now = performance.now();
        if (now - lastTap.current < 380) {
          setDraft(layer.name);
          setEditing(true);
        }
        lastTap.current = now;
      }}
    >
      {layer.name}
    </span>
  );
}

export function LayersPanel({ onClose }: { onClose: () => void }) {
  const ed = useEditor();
  const rows = useLayerRows(ed);
  const active = useUi((_, e) => e.activeLayerId());
  const hasSelection = useUi((ui) => ui.selection.length > 0);
  const readOnly = useUi((ui) => ui.readOnly);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // while dragging: which layer, and the slot (0 = top) it would drop into among the other rows
  const [drag, setDrag] = useState<{ id: string; slot: number; moved: boolean } | null>(null);

  // let the selection panel sit underneath this one
  useLayoutEffect(() => {
    const node = panelRef.current;
    const board = node?.closest(".board") as HTMLElement | null;
    if (!node || !board) return;
    const ro = new ResizeObserver(() => board.style.setProperty("--layers-h", node.offsetHeight + "px"));
    ro.observe(node);
    board.classList.add("layers-open");
    return () => {
      ro.disconnect();
      board.classList.remove("layers-open");
      board.style.removeProperty("--layers-h");
    };
  }, []);

  // rows are listed top layer first; the editor counts from the bottom
  const top = [...rows].reverse();
  const activeLayer = rows.find((r) => r.layer.id === active)?.layer ?? rows[rows.length - 1]?.layer;
  const activeIndex = rows.findIndex((r) => r.layer.id === activeLayer?.id);

  const startDrag = (e: React.PointerEvent, id: string, index: number) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag({ id, slot: index, moved: false });
  };
  const moveDrag = (e: React.PointerEvent) => {
    if (!drag || !listRef.current) return;
    let slot = 0;
    for (const row of listRef.current.querySelectorAll<HTMLElement>(".layer-row")) {
      if (row.dataset.layer === drag.id) continue;
      const r = row.getBoundingClientRect();
      if (e.clientY > r.top + r.height / 2) slot++;
    }
    if (slot !== drag.slot || !drag.moved) setDrag({ ...drag, slot, moved: true });
  };
  const endDrag = () => {
    if (!drag) return;
    if (drag.moved) {
      // slot counts rows from the top; the editor wants a position from the bottom of the other layers
      const others = rows.length - 1;
      ed.moveLayer(drag.id, others - drag.slot);
    }
    setDrag(null);
  };
  const others = top.filter((r) => r.layer.id !== drag?.id);

  return (
    <Panel ref={panelRef} className="layers-panel" seed="layers" tape="#ab9ac7" tapeSide="right" aria-label="Layers">
      <div className="layers-head">
        <h3>Layers</h3>
        {!readOnly && <IconButton icon={Plus} label="New layer" shortcut="⇧⌘N" size={19} onClick={() => ed.addLayer()} />}
        <IconButton icon={X} label="Close layers" size={18} onClick={onClose} />
      </div>
      {activeLayer && !readOnly && (
        <div className="layers-props">
          <label className="blend-select">
            <span className="sr-only">Blend mode</span>
            <select value={activeLayer.blend} onChange={(e) => ed.updateLayer(activeLayer.id, { blend: e.target.value as BlendMode })}>
              {BLEND_MODES.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <WavySlider
            value={activeLayer.opacity ?? 1}
            min={0.05}
            max={1}
            onChange={(v) => ed.updateLayer(activeLayer.id, { opacity: Math.round(v * 100) / 100 }, "layer-opacity-" + activeLayer.id)}
            label="Layer opacity"
            format={(v) => Math.round(v * 100) + "%"}
          />
        </div>
      )}
      <div className={"layers-list" + (drag ? " dragging" : "")} ref={listRef} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={() => setDrag(null)} role="listbox" aria-label="Layer stack">
        {top.map((r, i) => {
          const l = r.layer;
          const isActive = l.id === activeLayer?.id;
          const before = drag?.moved && drag.id !== l.id && others.indexOf(r) === drag.slot;
          const meta =
            `${r.count} ${r.count === 1 ? "thing" : "things"}` +
            (l.blend !== "normal" ? ` · ${BLEND_MODES.find((b) => b.key === l.blend)?.label.toLowerCase()}` : "") +
            ((l.opacity ?? 1) < 1 ? ` · ${Math.round((l.opacity ?? 1) * 100)}%` : "");
          return (
            <div
              key={l.id}
              data-layer={l.id}
              role="option"
              aria-selected={isActive}
              className={"layer-row" + (isActive ? " active" : "") + (l.visible ? "" : " hidden-layer") + (drag?.id === l.id && drag.moved ? " lifted" : "") + (before ? " drop-before" : "")}
              onClick={() => ed.setActiveLayer(l.id)}
            >
              {!readOnly && (
                <span className="layer-grip" onPointerDown={(e) => startDrag(e, l.id, i)} onClick={(e) => e.stopPropagation()} aria-label={"Drag to reorder " + l.name} title="Drag to reorder">
                  <GripVertical size={16} />
                </span>
              )}
              <LayerThumb layerId={l.id} />
              <span className="layer-text">
                {readOnly ? <span className="layer-name">{l.name}</span> : <LayerName layer={l} onRename={(name) => ed.updateLayer(l.id, { name })} />}
                <span className="layer-meta" title={meta}>
                  {r.hasSelection && <span className="layer-sel-dot" title="Part of the selection is here" />}
                  {meta}
                </span>
              </span>
              <button
                type="button"
                className={"layer-toggle" + (l.visible ? "" : " off")}
                aria-label={l.visible ? `Hide ${l.name}` : `Show ${l.name}`}
                aria-pressed={!l.visible}
                onClick={(e) => {
                  e.stopPropagation();
                  ed.updateLayer(l.id, { visible: !l.visible });
                }}
                disabled={readOnly}
              >
                {l.visible ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button
                type="button"
                className={"layer-toggle" + (l.locked ? " on" : "")}
                aria-label={l.locked ? `Unlock ${l.name}` : `Lock ${l.name}`}
                aria-pressed={!!l.locked}
                onClick={(e) => {
                  e.stopPropagation();
                  ed.updateLayer(l.id, { locked: !l.locked });
                }}
                disabled={readOnly}
              >
                {l.locked ? <Lock size={17} /> : <LockOpen size={17} />}
              </button>
            </div>
          );
        })}
        {drag?.moved && drag.slot === others.length && <div className="layer-drop-end" aria-hidden="true" />}
      </div>
      {!readOnly && activeLayer && (
        <div className="layers-actions">
          <IconButton icon={Copy} label="Duplicate layer" size={17} tipSide="top" onClick={() => ed.duplicateLayer(activeLayer.id)} />
          <IconButton icon={ArrowDownToLine} label="Merge down" size={17} tipSide="top" onClick={() => ed.mergeDown(activeLayer.id)} disabled={activeIndex <= 0} />
          <IconButton icon={SquareArrowDown} label="Move selection to this layer" size={17} tipSide="top" onClick={() => ed.moveSelectionToLayer(activeLayer.id)} disabled={!hasSelection} />
          <IconButton icon={MousePointerSquareDashed} label="Select everything on this layer" size={17} tipSide="top" onClick={() => ed.selectLayer(activeLayer.id)} />
          <IconButton
            icon={Trash2}
            label="Delete layer"
            size={17}
            tipSide="top"
            className="danger"
            disabled={rows.length <= 1}
            onClick={() => {
              const n = rows.find((r) => r.layer.id === activeLayer.id)?.count ?? 0;
              if (n && !window.confirm(`Delete “${activeLayer.name}” and the ${n} thing${n === 1 ? "" : "s"} on it? (Undo brings it back.)`)) return;
              ed.deleteLayer(activeLayer.id);
            }}
          />
        </div>
      )}
    </Panel>
  );
}
